import {
  addDays,
  addMonths,
  differenceInCalendarDays,
  differenceInYears,
  startOfDay,
  subYears,
} from 'date-fns';

import { parseCalendarDate } from '../date.ts';

/**
 * A birthday is upcoming through the same calendar day three months from
 * today, inclusive. `addMonths` clamps month-end dates, matching legacy
 * `DateTime.AddMonths` behavior (for example, November 30 → February 28).
 *
 * The comparison uses local calendar dates, consistent with `parseCalendarDate`
 * and the birthday age/display behavior used throughout ark-my.
 */
//TODO: Should probably be coming from SDG at some point
export const UPCOMING_BIRTHDAY_WINDOW_MONTHS = 3;

/**
 * These values provide guidance for when a birthday gift commitment created
 * at the same time as the related child sponsorship should charge for a gift
 * at the time of creation. The window days provide the time period both before
 * and after the birthday where it is considered near enough to give today. The
 * ending days is the length of time before and after the end of the window
 * where we will instruct SF to override it's pull day defaults.
 **/
const BIRTHDAY_PAYMENT_TODAY_WINDOW_DAYS = 60;
const BIRTHDAY_PAYMENT_TODAY_WINDOW_ENDING_DAYS = 3;

/**
 * Historical implementation of the same three-month requirement:
 *
 * `UPCOMING_BIRTHDAY_MONTHS_LOOKBACK = 3`
 * `monthsUntilBirthday = differenceInMonths(nextBirthday, today) + 1`
 *
 * The `+ 1` bucketed birthdays by partial months instead of comparing against
 * an exact three-calendar-month cutoff. The threshold remains three months;
 * only the boundary calculation has been corrected and centralized.
 */
// export const UPCOMING_BIRTHDAY_MONTHS_LOOKBACK = 3;

/**
 * A one-time birthday gift given within this many months still counts as
 * "covered" (so a recent donor isn't asked for another birthday gift).
 */
export const BIRTHDAY_GIFT_LOOKBACK_MONTHS = 5;

export const getAgeFromBirthday = (birthDate: string) => {
  const parsed = parseCalendarDate(birthDate);

  if (!parsed) {
    return '';
  }

  return differenceInYears(new Date(), parsed);
};

const isLeapYear = (year: number): boolean =>
  year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);

const parseBirthdateMonthDay = (
  birthdate: string,
): { monthIndex: number; day: number } | null => {
  const parsed = parseCalendarDate(birthdate);
  if (!parsed) {
    return null;
  }

  return { monthIndex: parsed.getMonth(), day: parsed.getDate() };
};

/**
 * Returns the next local-calendar occurrence of `birthdate`'s month/day.
 *
 * February 29 is observed on February 28 in non-leap years. Local calendar
 * operations keep this aligned with birthday age and display behavior.
 */
export const getNextBirthday = (
  birthdate: string,
  startDate = new Date(),
): Date | null => {
  const birthday = parseBirthdateMonthDay(birthdate);
  if (!birthday) {
    return null;
  }

  const today = startOfDay(startDate);
  const startYear = today.getFullYear();

  const occurrenceForYear = (year: number): Date => {
    const day =
      birthday.monthIndex === 1 && birthday.day === 29 && !isLeapYear(year)
        ? 28
        : birthday.day;
    return new Date(year, birthday.monthIndex, day);
  };

  let nextBirthday = occurrenceForYear(startYear);

  if (nextBirthday < today) {
    nextBirthday = occurrenceForYear(startYear + 1);
  }

  return nextBirthday;
};

/**
 * Whether the next occurrence of `birthdate` is within the inclusive,
 * three-calendar-month upcoming-birthday window. Invalid or missing birthdates
 * do not qualify.
 */
export const hasUpcomingBirthday = (
  birthdate: string | null | undefined,
  startDate = new Date(),
): boolean => {
  if (!birthdate) {
    return false;
  }

  const today = startOfDay(startDate);
  const nextBirthday = getNextBirthday(birthdate, today);

  return (
    nextBirthday !== null &&
    nextBirthday <= addMonths(today, UPCOMING_BIRTHDAY_WINDOW_MONTHS)
  );
};

/**
 * Comparator for sorting children by which birthday comes next.
 * Missing or invalid birthdates sort to the end.
 */
export const compareUpcomingBirthday = (
  a: string | null | undefined,
  b: string | null | undefined,
  startDate = new Date(),
): number => {
  const aTime = a
    ? (getNextBirthday(a, startDate)?.getTime() ?? Number.POSITIVE_INFINITY)
    : Number.POSITIVE_INFINITY;
  const bTime = b
    ? (getNextBirthday(b, startDate)?.getTime() ?? Number.POSITIVE_INFINITY)
    : Number.POSITIVE_INFINITY;

  return aTime === bTime ? 0 : aTime - bTime;
};

/**
 * Determines whether today is within the birthday gift payment window.
 * @param birthDate
 * @param today
 * @returns
 */
export const isWithinBirthdayPaymentWindow = (
  birthDate: string,
  today = new Date(),
): boolean => {
  const nextBirthday = getNextBirthday(birthDate, today);
  if (!nextBirthday) {
    return false;
  }
  const lastBirthday = subYears(nextBirthday, 1);

  const daysToNearest = Math.min(
    differenceInCalendarDays(nextBirthday, today),
    differenceInCalendarDays(today, lastBirthday),
  );
  return daysToNearest <= BIRTHDAY_PAYMENT_TODAY_WINDOW_DAYS;
};

/**
 * Determines whether today is within the critical ending region of the birthday gift payment window.
 * @param birthDate
 * @param today
 * @returns
 */
export const isAtBirthdayPaymentWindowEnd = (
  birthDate: string,
  today = new Date(),
): boolean => {
  const nextBirthday = getNextBirthday(birthDate, today);
  if (!nextBirthday) {
    return false;
  }
  const windowCenter = addDays(
    subYears(nextBirthday, 1),
    BIRTHDAY_PAYMENT_TODAY_WINDOW_DAYS,
  );
  return (
    Math.abs(differenceInCalendarDays(windowCenter, today)) <=
    BIRTHDAY_PAYMENT_TODAY_WINDOW_ENDING_DAYS
  );
};
